import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import LegalNotices from './components/LegalNotices';
import CookiePolicy from './components/CookiePolicy';
import CookieConsentBanner from './components/CookieConsentBanner';

const LegalComplianceFooter = () => {
  const [activeSection, setActiveSection] = useState('terms');
  const [showCookieBanner, setShowCookieBanner] = useState(true);

  const legalSections = [
    {
      id: 'terms',
      title: 'Conditions d\'Utilisation',
      icon: 'FileText',
      description: 'Terms governing platform usage, property responsibilities, and legal obligations'
    },
    {
      id: 'privacy',
      title: 'Politique de Confidentialité',
      icon: 'Shield',
      description: 'Data protection, GDPR compliance, and user information management'
    },
    {
      id: 'legal',
      title: 'Mentions Légales',
      icon: 'Scale',
      description: 'Business registration, licensing, and regulatory compliance information'
    },
    {
      id: 'cookies',
      title: 'Politique des Cookies',
      icon: 'Cookie',
      description: 'Tracking technologies, preferences, and opt-out mechanisms'
    }
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'terms':
        return <TermsOfService />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'legal':
        return <LegalNotices />;
      case 'cookies':
        return <CookiePolicy />;
      default:
        return <TermsOfService />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Legal Compliance - KAPITALSTATES</title>
        <meta name="description" content="Complete legal documentation for KAPITALSTATES - Terms of Service, Privacy Policy, Legal Notices, and Cookie Policy for Ivorian real estate operations." />
        <meta name="keywords" content="legal compliance, terms of service, privacy policy, legal notices, cookies, KAPITALSTATES, Côte d'Ivoire real estate" />
        <link rel="canonical" href="https://kapitalstates.com/legal-compliance-footer" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Cookie Consent Banner */}
        {showCookieBanner && (
          <CookieConsentBanner onClose={() => setShowCookieBanner(false)} />
        )}

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-brand-earth to-brand-earth/90 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-brand-gold rounded-full flex items-center justify-center">
                  <Icon name="Scale" size={32} color="var(--color-brand-earth)" />
                </div>
              </div>
              <h1 className="font-heading font-display-semibold text-3xl lg:text-4xl mb-6 text-white">
                Conformité Légale
              </h1>
              <p className="font-body text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
                Documentation légale complète pour les opérations immobilières KAPITALSTATES en Côte d'Ivoire. 
                Transparence, conformité et protection des utilisateurs au cœur de nos services.
              </p>
            </div>
          </div>
        </div>

        {/* Legal Navigation */}
        <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex overflow-x-auto py-4 space-x-1">
              {legalSections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`flex-shrink-0 flex items-center space-x-3 px-6 py-3 rounded-lg font-body font-medium transition-all duration-300 ${
                    activeSection === section?.id
                      ? 'bg-brand-gold text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-brand-earth'
                  }`}
                >
                  <Icon 
                    name={section?.icon} 
                    size={20} 
                    color={activeSection === section?.id ? 'white' : 'var(--color-gray-600)'} 
                  />
                  <span className="whitespace-nowrap">{section?.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legal Content */}
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-32">
                <h3 className="font-heading font-semibold text-lg text-brand-earth mb-4">
                  Sections Légales
                </h3>
                <nav className="space-y-2">
                  {legalSections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => setActiveSection(section?.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-300 ${
                        activeSection === section?.id
                          ? 'bg-brand-gold/10 text-brand-earth border-l-4 border-brand-gold' :'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <Icon 
                          name={section?.icon} 
                          size={18} 
                          color={activeSection === section?.id ? 'var(--color-brand-earth)' : 'var(--color-gray-500)'} 
                        />
                        <div>
                          <div className="font-medium text-sm">{section?.title}</div>
                          <div className="text-xs text-gray-500 mt-1 leading-relaxed">
                            {section?.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>

                {/* Quick Contact */}
                <div className="mt-8 p-4 bg-brand-earth/5 rounded-lg border border-brand-earth/10">
                  <h4 className="font-heading font-semibold text-brand-earth mb-2">
                    Questions Légales ?
                  </h4>
                  <p className="text-gray-600 text-sm mb-3">
                    Notre équipe juridique est à votre disposition
                  </p>
                  <Link to="/legal-services-center">
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      iconName="MessageCircle"
                      iconPosition="left"
                    >
                      Contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-sm">
                {renderActiveSection()}
              </div>
            </div>
          </div>
        </div>

        {/* Legal Footer Actions */}
        <div className="bg-brand-earth/5 border-t">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                <h3 className="font-heading font-semibold text-brand-earth mb-1">
                  Mise à jour: {new Date()?.toLocaleDateString('fr-FR')}
                </h3>
                <p className="text-gray-600 text-sm">
                  Documentation légale conforme aux réglementations ivoiriennes
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Icon name="Shield" size={16} color="var(--color-brand-gold)" />
                  <span>Conformité RGPD</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Icon name="Award" size={16} color="var(--color-brand-gold)" />
                  <span>Certifié Légal</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default LegalComplianceFooter;